import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HydrantBodService } from './HydrantBodService';
import { useToast } from '../../../components/toast/useToast';
import {
  CreateHydrantRequest, UpdateHydrantRequest, CreateHydrantKontrolaRequest,
  CreateHydrantPlanRequest, UpdateHydrantZavadaRequest,
} from '../generated/models/HydrantBod';

const KEY = 'hydranty-evidence';

export const useHydrantyQuery = () =>
  useQuery({ queryKey: [KEY], queryFn: HydrantBodService.getAll });

export const useHydrantDetailQuery = (id?: number) =>
  useQuery({ queryKey: [KEY, 'detail', id], queryFn: () => HydrantBodService.getDetail(id!), enabled: !!id });

export const useHydrantStatistikyQuery = () =>
  useQuery({ queryKey: [KEY, 'statistiky'], queryFn: HydrantBodService.getStatistiky });

export const useHydrantMapaQuery = () =>
  useQuery({ queryKey: [KEY, 'mapa'], queryFn: HydrantBodService.getMapa });

export const useChecklistQuery = (id?: number) =>
  useQuery({ queryKey: [KEY, 'checklist', id], queryFn: () => HydrantBodService.getChecklist(id!), enabled: !!id });

export const useHydrantKontrolyQuery = (id?: number) =>
  useQuery({ queryKey: [KEY, 'kontroly', id], queryFn: () => HydrantBodService.getKontroly(id!), enabled: !!id });

export const useHydrantPlanyQuery = () =>
  useQuery({ queryKey: [KEY, 'plany'], queryFn: HydrantBodService.getPlany });

export const useHydrantPlanDetailQuery = (planId?: number) =>
  useQuery({ queryKey: [KEY, 'plan', planId], queryFn: () => HydrantBodService.getPlanDetail(planId!), enabled: !!planId });

export const useHydrantZavadyQuery = () =>
  useQuery({ queryKey: [KEY, 'zavady'], queryFn: HydrantBodService.getZavady });

export const useCreateHydrantMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (data: CreateHydrantRequest) => HydrantBodService.create(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [KEY] }); showToast('Hydrant vytvořen', 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba při vytváření', 'error'),
  });
};

export const useUpdateHydrantMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHydrantRequest }) => HydrantBodService.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [KEY] }); showToast('Hydrant upraven', 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba při úpravě', 'error'),
  });
};

export const useDeleteHydrantMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (id: number) => HydrantBodService.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [KEY] }); showToast('Hydrant deaktivován', 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba při deaktivaci', 'error'),
  });
};

export const useImportGeoJsonMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (geojson: string) => HydrantBodService.importGeoJson(geojson),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [KEY] }); showToast(`Importováno ${r.vlozeno} hydrantů`, 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba importu', 'error'),
  });
};

export const useImportGpxMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (gpx: string) => HydrantBodService.importGpx(gpx),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: [KEY] }); showToast(`Importováno ${r.vlozeno} hydrantů`, 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba importu', 'error'),
  });
};

export const useProvestKontrolaMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateHydrantKontrolaRequest }) =>
      HydrantBodService.provestKontrolu(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [KEY] }); showToast('Kontrola uložena', 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba při ukládání kontroly', 'error'),
  });
};

export const useCreatePlanMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (data: CreateHydrantPlanRequest) => HydrantBodService.createPlan(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [KEY, 'plany'] }); showToast('Plán vytvořen', 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba při vytváření plánu', 'error'),
  });
};

export const useUpdateZavadaMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHydrantZavadaRequest }) =>
      HydrantBodService.updateZavada(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [KEY] }); showToast('Závada upravena', 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba při úpravě závady', 'error'),
  });
};

export const useUploadFotoMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ kontrolaId, file, popis }: { kontrolaId: number; file: File; popis?: string }) =>
      HydrantBodService.uploadFoto(kontrolaId, file, popis),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [KEY] }); showToast('Foto nahráno', 'success'); },
    onError: (e: Error) => showToast(e.message || 'Chyba nahrávání fota', 'error'),
  });
};

export const usePriraditKontroloraMutation = () => {
  const qc = useQueryClient();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: ({ polozkaId, data }: { polozkaId: number; data: { clenId?: number; jmeno?: string } }) =>
      HydrantBodService.priraditKontrolora(polozkaId, data),
    onSuccess: (_, { polozkaId }) => {
      qc.invalidateQueries({ queryKey: [KEY, 'plan'] });
      showToast('Kontrolor přiřazen', 'success');
    },
    onError: (e: Error) => showToast(e.message || 'Chyba při přiřazení', 'error'),
  });
};
